import { useState, useEffect } from 'react';
import { fxAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { TrendingUp, ArrowRightLeft, Clock } from 'lucide-react';
import { toast } from 'sonner';

export default function FXRatesPage() {
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [convertAmount, setConvertAmount] = useState('1000');
  const [convertFrom] = useState('USD');
  const [convertTo, setConvertTo] = useState('EUR');

  useEffect(() => {
    loadRates();
  }, []);

  const loadRates = async () => {
    try {
      const res = await fxAPI.rates();
      setRates(res.data.rates);
    } catch (err) {
      toast.error('Failed to load exchange rates');
    } finally {
      setLoading(false);
    }
  };

  const selectedRate = rates.find(r => r.currency_code === convertTo);
  const convertedValue = selectedRate ? (parseFloat(convertAmount) || 0) * selectedRate.rate : 0;

  if (loading) {
    return <div className="page-container"><p>Loading rates...</p></div>;
  }

  return (
    <div className="page-container">
      <h2 className="text-2xl font-semibold tracking-tight mb-6" style={{ fontFamily: 'Space Grotesk' }}>Currency Exchange Rates</h2>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Rates Table */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base" style={{ fontFamily: 'Space Grotesk' }}>Live Exchange Rates (Base: USD)</CardTitle>
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Clock className="h-3 w-3" />
                  Updated just now
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="divide-y">
                {rates.map(rate => (
                  <div key={rate.currency_code} className="flex items-center justify-between py-4 hover:bg-gray-50 px-2 rounded-lg transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                        <span className="text-sm font-bold text-[#0B5CAB]">{rate.symbol}</span>
                      </div>
                      <div>
                        <p className="text-sm font-medium">{rate.currency_code}</p>
                        <p className="text-xs text-muted-foreground">{rate.name}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-base font-semibold tabular-nums">{rate.rate}</p>
                      <p className="text-xs text-muted-foreground">1 USD = {rate.rate} {rate.currency_code}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Converter */}
        <div>
          <Card>
            <CardHeader>
              <CardTitle className="text-base" style={{ fontFamily: 'Space Grotesk' }}>Currency Converter</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Amount (USD)</Label>
                  <Input type="number" step="0.01" value={convertAmount} onChange={e => setConvertAmount(e.target.value)} />
                </div>
                <div className="flex items-center justify-center">
                  <ArrowRightLeft className="h-5 w-5 text-muted-foreground" />
                </div>
                <div className="space-y-2">
                  <Label>Convert To</Label>
                  <Select value={convertTo} onValueChange={setConvertTo}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {rates.map(r => (
                        <SelectItem key={r.currency_code} value={r.currency_code}>
                          {r.currency_code} - {r.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="p-4 rounded-xl bg-blue-50">
                  <p className="text-xs text-blue-600 font-medium">Converted Amount</p>
                  <p className="text-2xl font-semibold tabular-nums text-[#0B5CAB]">
                    {selectedRate?.symbol}{convertedValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Rate: 1 USD = {selectedRate?.rate} {convertTo}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="mt-4">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="h-4 w-4 text-[#0B5CAB]" />
                <p className="text-sm font-medium">Market Info</p>
              </div>
              <p className="text-xs text-muted-foreground">Rates are updated in real-time and reflect current market conditions. International transfers use these rates for currency conversion.</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
